import Stack from '@ahamove/web-components/lib/Stack';
import Typography from '@ahamove/web-components/lib/Typography';
import { useTranslation } from 'next-i18next';
import React from 'react';
import { useDateTimeStore } from '@/global-stores/useDateTimeStore';
import { formatFare } from '@/lib/common';
import { RentType } from '@/lib/constants';
import { homeConfig } from '@/modules/home/homeConfig';
import useIsMobile from '@/shared-hooks/useIsMobile';
import type { AvailableEV } from '../NewBookingProvider';

type Props = {
  data: AvailableEV;
};

export default function EVItemFee({ data }: Props) {
  const { t } = useTranslation(homeConfig.i18nNamespaces);
  const isMobile = useIsMobile();
  const { fareType } = useDateTimeStore();

  const hourlyFare = React.useMemo(
    () => fareType === 'hourly_fare',
    [fareType]
  );

  return (
    <Stack
      data-test-id="evItemFeeContainer"
      direction="column"
      spacing={isMobile ? 0 : 0.5}
    >
      {data?.service_type === RentType.BIKE ? (
        <>
          <Typography
            variant={isMobile ? 'h6' : 'h2'}
            fontSize={isMobile ? '16px' : '24px'}
            color="info.main"
            textAlign="left"
            sx={{
              marginBottom: isMobile ? '' : '20px !important',
            }}
          >
            {t('booking:ev.est')} {formatFare(data?.total_fee)}
          </Typography>
          <Typography
            variant={isMobile ? 'caption' : 'body1'}
            color="grey.700"
            fontWeight={600}
            textAlign="left"
            fontSize={isMobile ? '12px' : '14px'}
          >
            {hourlyFare
              ? `${formatFare(
                  data?.base_price_by_type?.hourly_fare?.first_unit_price
                )}/${t('booking:ev.first_hour')}`
              : `${formatFare(
                  data?.base_price_by_type?.daily_fare?.first_unit_price
                )}/${t('booking:ev.first_day')}`}
          </Typography>
          <Typography
            variant={isMobile ? 'caption' : 'body1'}
            color="grey.700"
            fontWeight={600}
            textAlign="left"
            fontSize={isMobile ? '12px' : '14px'}
          >
            {hourlyFare
              ? `${formatFare(
                  data?.base_price_by_type?.hourly_fare?.following_unit_price
                )}/${t('booking:ev.following_hour')}`
              : `${formatFare(
                  data?.base_price_by_type?.daily_fare?.following_unit_price
                )}/${t('booking:ev.following_day')}`}
          </Typography>
        </>
      ) : (
        <>
          <Typography
            variant={isMobile ? 'h6' : 'h2'}
            fontSize={isMobile ? '16px' : '24px'}
            color="info.main"
            textAlign="left"
          >
            {t('booking:ev.est')} {formatFare(data?.total_fee)}
          </Typography>
          <Typography
            variant={isMobile ? 'caption' : 'body1'}
            color="dark"
            fontWeight={600}
            fontSize={isMobile ? '12px' : '16px'}
            textAlign="left"
          >
            {t('booking:ev.surcharge_free', {
              number: data?.total_allow_distance_in_kilometer,
            })}
          </Typography>
          <Typography
            variant={isMobile ? 'caption' : 'body1'}
            color="grey.500"
            fontSize={12}
            fontWeight={600}
            lineHeight="20px"
            textAlign="left"
            sx={{
              marginTop: isMobile ? '12px !important' : '24px !important',
            }}
          >
            {t('booking:ev.extra_mileage_charges', {
              amount: formatFare(
                data?.fee_configs?.excess_distance_fee_per_kilometer
              ),
            })}
          </Typography>
        </>
      )}
    </Stack>
  );
}
